import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, type MeResponse } from '../game/api';
import { audio } from '../game/audio';
import { formatNumber, t, type Key } from '../i18n';
import { GroupPanel, type useGroup } from './GroupPanel';
import { Amount, Icon, TokenIcon } from './icons';
import { LegalLink, LegalLinks, legalText } from './LegalPage';
import { PageHead, type Page } from './MenuShell';

export function useMe() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const reload = useCallback(async () => {
    // Erreur passagère (réseau, limite de débit) : deux nouvelles tentatives espacées, l'état précédent est gardé.
    // C'est aussi /api/me qui pose le cookie d'invité : sans lui, les gains de la partie seraient perdus.
    for (let i = 0; i < 3; i++) {
      try {
        setMe(await apiGet<MeResponse>('/api/me'));
        return;
      } catch {
        await new Promise((r) => setTimeout(r, 2000 * (i + 1)));
      }
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { me, reload };
}

const TOKENS = ['tok_x2', 'tok_x5', 'tok_restore', 'tok_reset', 'tok_salon'] as const;

/** Copie dans le presse-papiers avec un retour visible (« Copié »). */
function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-ghost btn-small"
      aria-label={label}
      onClick={() => {
        void navigator.clipboard
          ?.writeText(text)
          .then(() => {
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          })
          .catch(() => {});
      }}
    >
      <Icon name={done ? 'check' : 'copy'} size={14} />
      {done ? t('copied') : t('copy')}
    </button>
  );
}

/** Profil : connexion ou inscription pour un invité ; identité, solde, jetons et compte pour un joueur connecté. */
export function ProfilePage({ me, onChanged, go }: { me: MeResponse | null; onChanged: () => void; go: (p: Page) => void }) {
  if (!me) return <div className="page profile-page"><p className="wip">{t('loading')}</p></div>;
  if (!me.account)
    return (
      <div className="page profile-page">
        <PageHead title={t('account')} sub={t('authSub')} />
        <div className="auth-layout">
          <AuthForms onDone={onChanged} guestCoins={me.balances.coins ?? 0} go={go} mail={me.mail !== false} />
          <section className="plate auth-perks" aria-labelledby="perks-title">
            <h2 className="plate-title" id="perks-title">
              {t('authPerksTitle')}
            </h2>
            <ul>
              <li>
                <Icon name="pass" size={18} />
                {t('authPerkPass')}
              </li>
              <li>
                <Icon name="tree" size={18} />
                {t('authPerkVariants')}
              </li>
              <li>
                <Icon name="friends" size={18} />
                {t('authPerkFriends')}
              </li>
              <li>
                <Icon name="shop" size={18} />
                {t('authPerkShop')}
              </li>
            </ul>
          </section>
        </div>
        <LegalLinks go={go} className="page-foot" />
        <DevMailbox />
      </div>
    );
  return <Profile me={me} onChanged={onChanged} go={go} />;
}

/** `mail` faux : aucun fournisseur d'e-mail (production sans Brevo) ; l'interface n'annonce alors aucun e-mail. */
function AuthForms({ onDone, guestCoins, go, mail }: { onDone: () => void; guestCoins: number; go: (p: Page) => void; mail: boolean }) {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg('');
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await apiPost('/api/forgot', { email });
        setMsg(mail ? t('forgotSent') : t('mailUnavailable'));
        return;
      }
      const r = await apiPost<{ error?: string }>(mode === 'login' ? '/api/login' : '/api/register', { email, password, name });
      if (r.status === 200) {
        setMsg(mode === 'register' ? (mail ? t('registered') : t('registeredNoMail')) : '');
        audio.play('uiReward');
        onDone();
      } else {
        audio.play('uiDeny');
        if (r.status === 429) setMsg(t('rateLimited'));
        else if (mode === 'login') setMsg(t('badCredentials'));
        else setMsg(t(`err_${r.data.error ?? 'server'}` as Key));
      }
    } finally {
      setBusy(false);
    }
  };
  const tab = (m: typeof mode, label: string) => (
    <button type="button" aria-pressed={mode === m} onClick={() => setMode(m)}>
      {label}
    </button>
  );
  return (
    <section className="plate auth-plate">
      <div className="seg auth-tabs" role="group" aria-label={t('account')}>
        {tab('login', t('login'))}
        {tab('register', t('register'))}
        {tab('forgot', t('forgot'))}
      </div>
      <form className="auth-form" onSubmit={submit}>
        <p className="wip">{t('guestInfo', { n: guestCoins })}</p>
        <label className="eyebrow" htmlFor="auth-email">
          {t('email')}
        </label>
        <input id="auth-email" type="email" required autoComplete="email" placeholder={t('email')} value={email} onChange={(e) => setEmail(e.target.value)} />
        {mode !== 'forgot' && (
          <>
            <label className="eyebrow" htmlFor="auth-password">
              {t('password')}
            </label>
            <input
              id="auth-password"
              type="password"
              required
              minLength={mode === 'register' ? 10 : 1}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              placeholder={t('password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </>
        )}
        {mode === 'register' && (
          <>
            <label className="eyebrow" htmlFor="auth-name">
              {t('displayName')}
            </label>
            <input id="auth-name" required minLength={3} maxLength={16} placeholder={t('displayName')} value={name} onChange={(e) => setName(e.target.value)} />
            <p className="wip">{t('authRegisterHint')}</p>
            <p className="wip auth-legal">{legalText(t('authLegal'), { privacy: <LegalLink page="privacy" go={go} />, terms: <LegalLink page="terms" go={go} /> })}</p>
          </>
        )}
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {t(mode === 'login' ? 'login' : mode === 'register' ? 'register' : 'sendReset')}
        </button>
        {msg && <p className="form-msg">{msg}</p>}
        <p className="wip">{t('oauthSoon')}</p>
      </form>
    </section>
  );
}

function Profile({ me, onChanged, go }: { me: MeResponse; onChanged: () => void; go: (p: Page) => void }) {
  const a = me.account!;
  const b = me.balances;
  const [resent, setResent] = useState(false);
  const [rename, setRename] = useState(a.name);
  const [renameMsg, setRenameMsg] = useState('');
  return (
    <div className="page profile-page">
      <PageHead title={t('tab_profile')} sub={t('profileSub')} />
      <div className="profile-grid">
        <section className="plate profile" aria-labelledby="profile-name">
          <div className="profile-head">
            <span className="profile-badge" aria-hidden>
              <Icon name="profile" size={30} />
            </span>
            <div className="profile-id">
              <strong className="display" id="profile-name">
                {a.name}
              </strong>
              <span className="profile-code">
                <code>{a.publicId}</code>
                <CopyButton text={a.publicId} label={t('copyId')} />
              </span>
            </div>
          </div>
          <div className="profile-line">
            <Icon name="mail" size={16} />
            <span className="profile-mail">{a.email}</span>
            <span className={`badge ${a.verified ? 'badge-ok' : 'badge-accent'}`}>{a.verified ? t('verified') : t('notVerified')}</span>
            {!a.verified && me.mail !== false && (
              <button
                type="button"
                className="btn btn-ghost btn-small"
                disabled={resent}
                onClick={async () => {
                  await apiPost('/api/verify/resend');
                  setResent(true);
                }}
              >
                {resent ? t('sent') : t('resend')}
              </button>
            )}
          </div>
          {!a.verified && <p className="wip">{me.mail === false ? t('mailUnavailable') : t('profileVerifyWhy')}</p>}
          <form
            className="rename-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const r = await apiPost<{ ok?: boolean }>('/api/rename', { name: rename });
              setRenameMsg(r.data.ok ? t('renameOk') : t('renameFail'));
              audio.play(r.data.ok ? 'uiClick' : 'uiDeny');
              if (r.data.ok) onChanged();
            }}
          >
            <label className="eyebrow" htmlFor="rename">
              {t('displayName')}
            </label>
            <div className="rename-row">
              <input id="rename" className="field" minLength={3} maxLength={16} value={rename} onChange={(e) => setRename(e.target.value)} />
              <button type="submit" className="btn btn-small" disabled={rename.trim() === a.name || rename.trim().length < 3}>
                {t('renameBtn')}
              </button>
            </div>
            {renameMsg && <p className="wip">{renameMsg}</p>}
          </form>
          <button
            type="button"
            className="btn btn-ghost btn-small logout"
            onClick={async () => {
              await apiPost('/api/logout');
              onChanged();
            }}
          >
            <Icon name="exit" size={16} />
            {t('logout')}
          </button>
        </section>

        <section className="plate wallet-plate" aria-labelledby="wallet-title">
          <h2 className="plate-title" id="wallet-title">
            {t('profileWallet')}
          </h2>
          <div className="wallet-big">
            <Amount kind="coins" n={b.coins ?? 0} size={28} />
            <Amount kind="gems" n={b.gems ?? 0} size={28} />
          </div>
          <h3 className="eyebrow">{t('tokensTitle')}</h3>
          <ul className="token-list">
            {TOKENS.map((k) => (
              <li key={k} className={b[k] ? '' : 'empty'}>
                <TokenIcon token={k} size={30} />
                <span>{t(`token_${k}` as Key)}</span>
                <strong className="num">×{formatNumber(b[k] ?? 0)}</strong>
              </li>
            ))}
          </ul>
          {me.roulette && <p className="wip">{t('rouletteWeek', { n: me.roulette.used, max: me.roulette.max })}</p>}
        </section>

        <section className="plate profile-data" aria-labelledby="profile-data-title">
          <h2 className="plate-title" id="profile-data-title">
            {t('profileDataTitle')}
          </h2>
          <p>{legalText(t('profileData'))}</p>
          <LegalLinks go={go} />
        </section>
      </div>
      <DevMailbox />
    </div>
  );
}

/** Amis et groupe : ajouter par identifiant public, accepter, retirer, bloquer ; groupe du lobby. */
export function FriendsPage({ me, group, go }: { me: MeResponse | null; group: ReturnType<typeof useGroup>; go: (p: Page) => void }) {
  const account = me?.account;
  return (
    <div className="page friends-page">
      <PageHead title={t('nav_friends')} sub={t('friendsSub')} />
      {!account ? (
        <section className="plate shop-gate">
          <p className="wip">{t('friendsGuest')}</p>
          <button type="button" className="btn btn-small" onClick={() => go('profile')}>
            <Icon name="profile" size={16} />
            {t('register')}
          </button>
        </section>
      ) : (
        <div className="friends-grid">
          <Friends myId={account.publicId} />
          <GroupPanel me={account} group={group} />
        </div>
      )}
    </div>
  );
}

function Friends({ myId }: { myId: string }) {
  const [list, setList] = useState<{ publicId: string; name: string; status: string; online: boolean }[]>([]);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try {
      setList((await apiGet<{ friends: typeof list }>('/api/friends')).friends ?? []);
    } catch {
      /* nouvel essai au prochain affichage */
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const act = async (path: string, publicId: string) => {
    const r = await apiPost<{ result?: string }>(path, { publicId });
    if (r.data.result) setMsg(t(`friend_${r.data.result}` as Key));
    await load();
  };
  const order = { incoming: 0, friend: 1, outgoing: 2 } as Record<string, number>;
  const sorted = [...list].sort((x, y) => (order[x.status] ?? 3) - (order[y.status] ?? 3) || Number(y.online) - Number(x.online));
  return (
    <section className="plate friends" aria-labelledby="friends-title">
      <h2 className="plate-title" id="friends-title">
        {t('nav_friends')}
        <span className="badge num">{list.filter((f) => f.status === 'friend').length}</span>
      </h2>
      <div className="my-id">
        <span className="eyebrow">{t('friendsYourId')}</span>
        <code>{myId}</code>
        <CopyButton text={myId} label={t('copyId')} />
      </div>
      <form
        className="add-friend"
        onSubmit={(e) => {
          e.preventDefault();
          void act('/api/friends/request', code);
        }}
      >
        <input className="field" placeholder="TS-XXXXXXXX" aria-label={t('friendsAddLabel')} value={code} onChange={(e) => setCode(e.target.value)} spellCheck={false} />
        <button className="btn btn-small" type="submit" disabled={!code.trim()}>
          <Icon name="plus" size={14} />
          {t('addFriend')}
        </button>
      </form>
      {msg && <p className="form-msg">{msg}</p>}
      {list.length === 0 && <p className="wip">{t('noFriends')}</p>}
      <ul className="friend-list">
        {sorted.map((f) => (
          <li key={f.publicId} className={`friend-row ${f.status}`}>
            <span className={`dot ${f.online ? 'on' : ''}`} role="img" aria-label={f.online ? t('online') : t('offline')} />
            <span className="friend-name">
              <strong>{f.name}</strong>
              <code>{f.publicId}</code>
            </span>
            <span className={`badge ${f.status === 'incoming' ? 'badge-accent' : ''}`}>{t(`fstatus_${f.status}` as Key)}</span>
            <span className="friend-actions">
              {f.status === 'incoming' && (
                <button type="button" className="btn btn-small btn-accept" onClick={() => act('/api/friends/accept', f.publicId)}>
                  {t('accept')}
                </button>
              )}
              <button type="button" className="btn btn-ghost btn-small" onClick={() => act('/api/friends/remove', f.publicId)}>
                {t('remove')}
              </button>
              <button type="button" className="btn btn-ghost btn-small btn-danger" onClick={() => act('/api/block', f.publicId)}>
                {t('block')}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Boîte de test des e-mails : affichée seulement quand le serveur la propose (développement, aucun e-mail réel). */
function DevMailbox() {
  const [mails, setMails] = useState<{ to: string; subject: string; body: string; at: string }[] | null>(null);
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    void fetch('/api/dev/mailbox')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!live || !d) return;
        setAvailable(true);
        setMails(d.mails ?? []);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [open]);
  if (!available) return null;
  return (
    <details className="dev-mailbox" onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>{t('devMailbox')}</summary>
      {(mails ?? []).map((m, i) => (
        <div key={i} className="mail">
          <strong>{m.subject}</strong> → {m.to}
          <p>
            {m.body.split(/(https?:\/\/\S+)/).map((part, j) =>
              /^https?:\/\//.test(part) ? (
                <a key={j} href={part}>
                  {part}
                </a>
              ) : (
                part
              ),
            )}
          </p>
        </div>
      ))}
    </details>
  );
}
